import React, { FunctionComponent, useEffect } from "react";
import { Form, Input, Modal } from "antd";
import getQuestionComponent from "../../getQuestionComponent";
import { buildFieldValuesFromDraft } from "../../formUtils";
import { V2Question, V2Response } from "../../helper";
import roundedStyles from "../roundedModal.module.scss";
import styles from "./SubmissionPreview.module.scss";

export interface SubmittedResponse {
  questionId: string;
  question: { id: string; label: string; questionType: string } | null;
  answer: string | number | boolean | string[] | null;
  fileUrls?: string[];
}

export interface SubmissionData {
  id: string;
  status: string;
  submittedAt?: string | null;
  metadata?: { jobForm?: { companyName?: string; jobTitle?: string } } | null;
  responses: SubmittedResponse[];
}

interface SubmissionPreviewProps {
  open?: boolean;
  onClose?: () => void;
  // `inline` renders the read-only form directly (no modal) — used by the admin "view response" drawer.
  inline?: boolean;
  // Full form questions (for rendering the read-only inputs) + the user's submission (for prefill).
  questions: V2Question[];
  submission: SubmissionData | null;
}

/**
 * Read-only preview of a submitted job-form response: renders the exact form UI (Company / Job Title
 * + each question) via the shared question components in `disabled` mode, prefilled with the
 * submitted answers. No submit button.
 */
const SubmissionPreview: FunctionComponent<SubmissionPreviewProps> = ({ open, onClose, inline = false, questions, submission }) => {
  const [form] = Form.useForm();

  const sortedQuestions = [...(questions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Prefill the disabled form with the submitted answers (each time the modal opens, or on mount when inline).
  useEffect(() => {
    if ((open || inline) && submission) {
      form.setFieldsValue(buildFieldValuesFromDraft(sortedQuestions, submission.responses as V2Response[]));
    }
  }, [open, inline, submission, form]);

  if (!submission) return null;
  const jobForm = submission.metadata?.jobForm;

  const body = (
    <Form form={form} layout="vertical" requiredMark={false} className={styles.previewForm}>
      {/* Company / Job Title — read-only */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Form.Item label="Company" className="flex-1 !mb-0">
          <Input value={jobForm?.companyName || "—"} disabled />
        </Form.Item>
        <Form.Item label="Job Title" className="flex-1 !mb-0">
          <Input value={jobForm?.jobTitle || "—"} disabled />
        </Form.Item>
      </div>

      {/* Admin-built questions — the real inputs, disabled + prefilled */}
      <div className="mt-4 flex flex-col gap-4">
        {sortedQuestions.map(question => (
          <div key={question.id}>{getQuestionComponent({ question, disabled: true, formType: "SURVEY" })}</div>
        ))}
      </div>
    </Form>
  );

  if (inline) return body;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Your response"
      footer={null}
      width={560}
      centered
      destroyOnClose
      className={roundedStyles.roundedModal}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      {body}
    </Modal>
  );
};

export default SubmissionPreview;
